import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
import { useCallRecordingControllerGetCallRecordingQuery } from '@shared/services/apiService/apiService';
import { formatRecordingTime } from './recordingSlot.utils';

interface RecordingSlotProps {
  callId: string;
  hasRecording: boolean;
  onPlaybackTimeChange?: (currentTime: number, duration: number) => void;
}

export interface RecordingSlotHandle {
  seekTo: (seconds: number) => void;
}

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2];

export const RecordingSlot = forwardRef<RecordingSlotHandle, RecordingSlotProps>(
  ({ callId, hasRecording, onPlaybackTimeChange }, ref) => {
    const { t } = useTranslation();
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const waveSurferRef = useRef<WaveSurfer | null>(null);
    const retryRef = useRef(false);
    const positionRef = useRef(0);
    const wasPlayingRef = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [playerError, setPlayerError] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const {
      data: recording,
      isError: isRecordingError,
      isLoading: isRecordingLoading,
      refetch,
    } = useCallRecordingControllerGetCallRecordingQuery(
      { id: callId },
      { skip: !callId, refetchOnMountOrArgChange: true },
    );
    const recordingUrl = recording?.url;

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (seconds: number) => {
          const waveSurfer = waveSurferRef.current;
          if (!waveSurfer || !Number.isFinite(seconds)) return;
          const maxDuration = waveSurfer.getDuration();
          waveSurfer.setTime(Math.max(0, Math.min(seconds, maxDuration || seconds)));
        },
      }),
      [],
    );

    const retryRecording = useCallback(async () => {
      if (retryRef.current) {
        setPlayerError(true);
        return;
      }
      retryRef.current = true;
      try {
        await refetch().unwrap();
        setPlayerError(false);
        if (waveSurferRef.current && recordingUrl) {
          await waveSurferRef.current.load(recordingUrl);
        }
      } catch {
        setPlayerError(true);
      }
    }, [recordingUrl, refetch]);

    useEffect(() => {
      retryRef.current = false;
      setPlayerError(false);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
    }, [callId]);

    useEffect(() => {
      if (!recording?.expiresAt) return;
      const expiresAt = new Date(recording.expiresAt).getTime();
      const timer = window.setTimeout(
        () => void refetch(),
        Math.max(1000, expiresAt - Date.now() - 30_000),
      );
      return () => window.clearTimeout(timer);
    }, [recording?.expiresAt, refetch]);

    useEffect(() => {
      if (!waveformRef.current || waveSurferRef.current) return;
      try {
        const waveSurfer = WaveSurfer.create({
          container: waveformRef.current,
          height: 56,
          barWidth: 2,
          barGap: 2,
          barRadius: 2,
          waveColor: '#b8c1d1',
          progressColor: '#635bff',
          cursorColor: '#635bff',
          normalize: true,
        });
        waveSurfer.on('ready', (readyDuration) => {
          setDuration(readyDuration);
          onPlaybackTimeChange?.(waveSurfer.getCurrentTime(), readyDuration);
          setPlayerError(false);
        });
        waveSurfer.on('play', () => setIsPlaying(true));
        waveSurfer.on('pause', () => setIsPlaying(false));
        waveSurfer.on('finish', () => setIsPlaying(false));
        waveSurfer.on('timeupdate', (time) => {
          positionRef.current = time;
          setCurrentTime(time);
          onPlaybackTimeChange?.(time, waveSurfer.getDuration());
        });
        waveSurfer.on('error', () => {
          setPlayerError(true);
          void retryRecording();
        });
        waveSurferRef.current = waveSurfer;
      } catch {
        setPlayerError(true);
      }
      return () => {
        waveSurferRef.current?.destroy();
        waveSurferRef.current = null;
      };
    }, [onPlaybackTimeChange, retryRecording]);

    useEffect(() => {
      if (!recordingUrl || !waveSurferRef.current) return;
      const waveSurfer = waveSurferRef.current;
      const position = positionRef.current;
      const wasPlaying = wasPlayingRef.current;
      setPlayerError(false);
      void waveSurfer.load(recordingUrl).then(() => {
        if (position > 0 && waveSurfer.getDuration() > 0) {
          waveSurfer.setTime(Math.min(position, waveSurfer.getDuration()));
        }
        if (wasPlaying) void waveSurfer.play();
      });
    }, [recordingUrl]);

    const handlePlayPause = () => {
      wasPlayingRef.current = !isPlaying;
      waveSurferRef.current?.playPause();
    };

    const handleRateChange = (rate: number) => {
      setPlaybackRate(rate);
      waveSurferRef.current?.setPlaybackRate(rate);
    };

    const handleDownload = async () => {
      if (!recordingUrl || isDownloading) return;

      setIsDownloading(true);
      setDownloadError(false);
      try {
        const response = await fetch(recordingUrl);
        if (!response.ok) throw new Error(`Recording download failed: ${response.status}`);

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        const extension = blob.type.split('/')[1] || 'ogg';
        link.download = `call-${callId}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Let browser start download before releasing temporary URL.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch {
        setDownloadError(true);
      } finally {
        setIsDownloading(false);
      }
    };

    if (isRecordingLoading && hasRecording) {
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} minHeight={56}>
            <CircularProgress size={20} />
            <Typography>{t('campaignPage.callDetailHeader.recordingLoading')}</Typography>
          </Stack>
        </Paper>
      );
    }
    if (!hasRecording || isRecordingError || !recordingUrl) {
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t('campaignPage.callDetailHeader.recordingUnavailable')}
          </Typography>
        </Paper>
      );
    }
    if (playerError) {
      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t('campaignPage.callDetailHeader.recordingUnavailable')}
          </Typography>
        </Paper>
      );
    }
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Box ref={waveformRef} aria-label={t('campaignPage.callDetailHeader.waveform')} />
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={handlePlayPause}
              aria-label={
                isPlaying
                  ? t('campaignPage.callDetailHeader.pause')
                  : t('campaignPage.callDetailHeader.play')
              }
              size="small">
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Typography variant="caption" color="text.secondary" minWidth={92}>
              {formatRecordingTime(currentTime)} / {formatRecordingTime(duration)}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 82 }}>
              <Select
                value={playbackRate}
                onChange={(event) => handleRateChange(Number(event.target.value))}
                aria-label={t('campaignPage.callDetailHeader.speed')}>
                {PLAYBACK_RATES.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {rate}x
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box flexGrow={1} />
            <Tooltip title={t('campaignPage.callDetailHeader.download')} arrow>
              <IconButton
                onClick={() => void handleDownload()}
                disabled={isDownloading}
                aria-label={t('campaignPage.callDetailHeader.download')}
                size="small">
                {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
          {downloadError && (
            <Alert severity="error" sx={{ py: 0 }}>
              {t('campaignPage.callDetailHeader.recordingUnavailable')}
            </Alert>
          )}
        </Stack>
      </Paper>
    );
  },
);

RecordingSlot.displayName = 'RecordingSlot';
